<template>
  <div class="page-content">
    <div class="operation-logs">
      <div class="page-header">
        <h2 class="page-title">用户操作日志</h2>
        <button class="back-btn" @click="goBack">
          <i class="icon">←</i>
          <span>返回</span>
        </button>
      </div>

      <!-- 筛选区域 -->
      <div class="filter-section">
        <div class="filter-container">
          <div class="custom-select">
            <div class="select-trigger" @click="toggleOperationTypeDropdown" :class="{ active: showOperationTypeDropdown }">
              <span class="select-value">{{ selectedOperationType || '全部操作类型' }}</span>
              <i class="select-arrow" :class="{ rotated: showOperationTypeDropdown }">▼</i>
            </div>
            <div v-if="showOperationTypeDropdown" class="select-options">
              <div class="select-option" :class="{ selected: !selectedOperationType }" @click="selectOperationType('')">
                <span class="option-text">全部操作类型</span>
              </div>
              <div class="select-option" :class="{ selected: selectedOperationType === '新增用户' }" @click="selectOperationType('新增用户')">
                <span class="option-text">新增用户</span>
              </div>
              <div class="select-option" :class="{ selected: selectedOperationType === '续费' }" @click="selectOperationType('续费')">
                <span class="option-text">续费</span>
              </div>
              <div class="select-option" :class="{ selected: selectedOperationType === '增加窗口且交钱' }" @click="selectOperationType('增加窗口且交钱')">
                <span class="option-text">增加窗口且交钱</span>
              </div>
              <div class="select-option" :class="{ selected: selectedOperationType === '调整窗口容量' }" @click="selectOperationType('调整窗口容量')">
                <span class="option-text">调整窗口容量</span>
              </div>
            </div>
          </div>
          <input 
            v-model="operatorUsernameFilter" 
            type="text" 
            placeholder="搜索操作人..." 
            class="search-input"
            @input="handleFilterChange"
          />
          <input 
            v-model="targetUsernameFilter" 
            type="text" 
            placeholder="搜索目标用户..." 
            class="search-input"
            @input="handleFilterChange"
          />
          <button
            class="reset-filter-btn"
            @click="handleResetFilters"
            v-if="selectedOperationType || operatorUsernameFilter || targetUsernameFilter"
          >
            <i class="icon">🔄</i>
            <span>重置</span>
          </button>
        </div>
      </div>

      <!-- 日志列表 -->
      <div class="logs-container">
        <div v-if="loading" class="loading-container">
          <div class="loading-spinner">
            <div class="spinner"></div>
            <p>加载中...</p>
          </div>
        </div>
        <div v-else class="content-container">
          <div class="table-container">
            <div class="table-header">
              <div class="header-cell">操作时间</div>
              <div class="header-cell">操作类型</div>
              <div class="header-cell">操作人</div>
              <div class="header-cell">目标用户</div>
              <div class="header-cell">窗口变化</div>
              <div class="header-cell">到期时间变化</div>
              <div class="header-cell">续费天数</div>
              <div class="header-cell">用户类型</div>
              <div class="header-cell">游戏</div>
              <div class="header-cell">备注</div>
            </div>
            <div class="table-body">
              <div v-if="logList.length === 0" class="empty-state">
                <div class="empty-icon">📋</div>
                <div class="empty-text">暂无操作日志</div>
              </div>
              <div v-else v-for="log in logList" :key="log.id" class="table-row">
                <div class="cell">{{ formatDateTime(log.create_time) }}</div>
                <div class="cell">
                  <span :class="['operation-type-badge', getOperationTypeClass(log.operation_type)]">
                    {{ log.operation_type }}
                  </span>
                </div>
                <div class="cell">{{ log.operator_username }}</div>
                <div class="cell">{{ log.target_username }}</div>
                <div class="cell">
                  <span v-if="log.old_window_count !== null && log.new_window_count !== null">
                    {{ log.old_window_count }} → {{ log.new_window_count }}
                    <span v-if="log.added_window_count" class="added-count">(+{{ log.added_window_count }})</span>
                  </span>
                  <span v-else-if="log.new_window_count !== null">
                    新增: {{ log.new_window_count }}
                  </span>
                  <span v-else>-</span>
                </div>
                <div class="cell">
                  <span v-if="log.old_expiry_date && log.new_expiry_date">
                    {{ formatDate(log.old_expiry_date) }} → {{ formatDate(log.new_expiry_date) }}
                  </span>
                  <span v-else-if="log.new_expiry_date">
                    {{ formatDate(log.new_expiry_date) }}
                  </span>
                  <span v-else>-</span>
                </div>
                <div class="cell">{{ log.renew_days ? log.renew_days + '天' : '-' }}</div>
                <div class="cell">{{ log.user_type || '-' }}</div>
                <div class="cell">{{ log.game_name || '-' }}</div>
                <div class="cell remark-cell">{{ log.remark || '-' }}</div>
              </div>
            </div>
          </div>

          <!-- 分页 -->
          <div v-if="totalPages > 1" class="pagination-container">
            <div class="pagination-info">
              共 {{ totalRecords }} 条记录，第 {{ currentPage }} / {{ totalPages }} 页
            </div>
            <div class="pagination-controls">
              <button 
                class="page-btn" 
                :disabled="currentPage === 1" 
                @click="goToPage(currentPage - 1)"
              >
                上一页
              </button>
              <button 
                class="page-btn" 
                :disabled="currentPage === totalPages" 
                @click="goToPage(currentPage + 1)"
              >
                下一页
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { userAPI } from '../api/users'

export default {
  name: 'UserOperationLogs',
  setup() {
    const router = useRouter()
    
    // 数据
    const loading = ref(false)
    const logList = ref([])
    const currentPage = ref(1)
    const pageSize = ref(20)
    const totalRecords = ref(0)
    const totalPages = ref(0)
    
    // 筛选条件
    const selectedOperationType = ref('')
    const operatorUsernameFilter = ref('')
    const targetUsernameFilter = ref('')
    const showOperationTypeDropdown = ref(false)
    
    // 计算属性
    const computedTotalPages = computed(() => {
      return totalPages.value
    })
    
    // 加载日志数据
    const loadLogs = async () => {
      loading.value = true
      try {
        const response = await userAPI.getOperationLogs(
          currentPage.value,
          pageSize.value,
          selectedOperationType.value || null,
          operatorUsernameFilter.value || null,
          targetUsernameFilter.value || null
        )
        
        // axios拦截器已经返回了response.data，所以response就是ApiResponse对象
        console.log('操作日志API响应:', response)
        
        if (response && response.code === 200) {
          const data = response.data
          console.log('操作日志数据:', data)
          
          if (data) {
            // MyBatis Plus的IPage对象字段：records, total, pages, size, current
            logList.value = data.records || []
            totalRecords.value = data.total || 0
            totalPages.value = data.pages || 0
            
            console.log('解析后的数据 - 记录数:', logList.value.length, '总数:', totalRecords.value, '总页数:', totalPages.value)
          } else {
            logList.value = []
            totalRecords.value = 0
            totalPages.value = 0
          }
        } else {
          console.error('API返回错误:', response)
          ElMessage.error(response?.message || '加载日志失败')
        }
      } catch (error) {
        console.error('加载日志失败:', error)
        ElMessage.error(error.response?.data?.message || error.message || '加载日志失败')
      } finally {
        loading.value = false
      }
    }
    
    // 筛选操作
    const toggleOperationTypeDropdown = () => {
      showOperationTypeDropdown.value = !showOperationTypeDropdown.value
    }
    
    const selectOperationType = (type) => {
      selectedOperationType.value = type
      showOperationTypeDropdown.value = false
      currentPage.value = 1
      loadLogs()
    }
    
    const handleFilterChange = () => {
      currentPage.value = 1
      loadLogs()
    }
    
    const handleResetFilters = () => {
      selectedOperationType.value = ''
      operatorUsernameFilter.value = ''
      targetUsernameFilter.value = ''
      currentPage.value = 1
      loadLogs()
    }
    
    // 分页操作
    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page
        loadLogs()
      }
    }
    
    // 格式化函数
    const formatDateTime = (dateTime) => {
      if (!dateTime) return '-'
      const date = new Date(dateTime)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    }
    
    const formatDate = (dateTime) => {
      if (!dateTime) return '-'
      const date = new Date(dateTime)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
    
    const getOperationTypeClass = (type) => {
      const classMap = {
        '新增用户': 'type-create',
        '续费': 'type-renew',
        '增加窗口且交钱': 'type-expand',
        '调整窗口容量': 'type-adjust'
      }
      return classMap[type] || ''
    }
    
    // 返回
    const goBack = () => {
      router.push('/dashboard/users')
    }
    
    // 初始化
    onMounted(() => {
      loadLogs()
    })
    
    return {
      loading,
      logList,
      currentPage,
      totalPages,
      totalRecords,
      selectedOperationType,
      operatorUsernameFilter,
      targetUsernameFilter,
      showOperationTypeDropdown,
      computedTotalPages,
      toggleOperationTypeDropdown,
      selectOperationType,
      handleFilterChange,
      handleResetFilters,
      goToPage,
      formatDateTime,
      formatDate,
      getOperationTypeClass,
      goBack
    }
  }
}
</script>

<style scoped>
.page-content {
  padding: 20px 20px 32px;
  background: #f5f5f5;
  box-sizing: border-box;
}

.operation-logs {
  max-width: 1400px;
  margin: 0 auto;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
  background: white;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #f0f0f0;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: #666;
  transition: all 0.2s;
}

.back-btn:hover {
  background: #e0e0e0;
  color: #333;
}

.filter-section {
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
  background: #fafafa;
}

.filter-container {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.custom-select {
  position: relative;
  min-width: 160px;
}

.select-trigger {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.select-trigger:hover {
  border-color: #409EFF;
}

.select-trigger.active {
  border-color: #409EFF;
}

.select-value {
  color: #333;
}

.select-arrow {
  font-size: 12px;
  color: #999;
  transition: transform 0.2s;
}

.select-arrow.rotated {
  transform: rotate(180deg);
}

.select-options {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
}

.select-option {
  padding: 10px 12px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s;
}

.select-option:hover {
  background: #f0f0f0;
}

.select-option.selected {
  background: #e6f7ff;
  color: #409EFF;
}

.search-input {
  flex: 1;
  min-width: 150px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
}

.search-input:focus {
  outline: none;
  border-color: #409EFF;
}

.reset-filter-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #f0f0f0;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: #666;
  transition: all 0.2s;
}

.reset-filter-btn:hover {
  background: #e0e0e0;
  color: #333;
}

.logs-container {
  padding: 20px 24px;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
}

.loading-spinner {
  text-align: center;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #409EFF;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.table-container {
  overflow-x: auto;
}

.table-header {
  display: grid;
  grid-template-columns: 160px 120px 120px 120px 140px 200px 100px 100px 120px 200px;
  gap: 1px;
  background: #e0e0e0;
  border-radius: 6px 6px 0 0;
  overflow: hidden;
}

.header-cell {
  padding: 12px;
  background: #f5f5f5;
  font-weight: 600;
  font-size: 14px;
  color: #333;
  text-align: center;
}

.table-body {
  background: white;
}

.table-row {
  display: grid;
  grid-template-columns: 160px 120px 120px 120px 140px 200px 100px 100px 120px 200px;
  gap: 1px;
  background: #e0e0e0;
  border-top: 1px solid #e0e0e0;
}

.table-row:hover {
  background: #f0f7ff;
}

.cell {
  padding: 12px;
  background: white;
  font-size: 14px;
  color: #666;
  text-align: center;
  word-break: break-word;
}

.remark-cell {
  text-align: left;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.operation-type-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.operation-type-badge.type-create {
  background: #e6f7ff;
  color: #1890ff;
}

.operation-type-badge.type-renew {
  background: #fff7e6;
  color: #fa8c16;
}

.operation-type-badge.type-expand {
  background: #f6ffed;
  color: #52c41a;
}

.operation-type-badge.type-adjust {
  background: #fff0f6;
  color: #eb2f96;
}

.added-count {
  color: #52c41a;
  font-weight: 500;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.empty-text {
  font-size: 16px;
  color: #999;
}

.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
}

.pagination-info {
  font-size: 14px;
  color: #666;
}

.pagination-controls {
  display: flex;
  gap: 12px;
}

.page-btn {
  padding: 8px 16px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: #333;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  background: #f0f0f0;
  border-color: #409EFF;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>

